export interface SchemaEventPayload {
    uri: string;
    data: Record<string, string>;
}

const URI_DOMAIN = 'der:tailormade';
const URI_CONTEXT = 'roundtrip';
const URI_DOMAIN_SEGMENTS = URI_DOMAIN.split(':').filter(Boolean);
const MFE_NAME = String(import.meta.env?.VITE_MFE_NAME ?? 'der-tm-roundtrip-stage').replace('@dertour/', '');

/**
 * Checks if the Schema.org listener is ready to receive events.
 *
 * @returns boolean
 */
function isSchemaOrgListenerReady(): boolean {
    return (window as unknown as { schemaInjectorReady?: boolean }).schemaInjectorReady === true;
}

/**
 * Dispatches a custom event with the raw payload for roundtrip stage schema.
 *
 * @param payload SchemaEventPayload The typed payload to be dispatched.
 * @param dispatchTarget Optional event target for dispatching the schema event.
 */
export function triggerSchemaJsonEvent(payload: SchemaEventPayload, dispatchTarget: EventTarget = window): void {
    const event = new CustomEvent('der-tm:schema', {
        detail: payload,
        bubbles: true,
        composed: true,
    });

    if (isSchemaOrgListenerReady()) {
        dispatchTarget.dispatchEvent(event);
    } else {
        const retryDelay = 500; // ms
        const maxRetries = 10;
        let attempts = 0;

        const checkAndDispatch = () => {
            if (isSchemaOrgListenerReady()) {
                dispatchTarget.dispatchEvent(event);
            } else if (attempts < maxRetries) {
                attempts++;
                setTimeout(checkAndDispatch, retryDelay);
            }
        };

        setTimeout(checkAndDispatch, retryDelay);
    }
}

/**
 * Function to intercept 'der-tm:schema' events from child elements,
 * stop their propagation, and modify the event detail with a constructed URI.
 * @param element HtmlElement to attach the event listener to
 * @returns Cleanup function to remove the registered event listener
 */
export function interceptChildEvents(element: HTMLElement): () => void {
    const handler = (event: Event) => {
        const origin = event.composedPath()[0];

        if (origin === element) {
            return;
        }

        const uri = updateUri((event as CustomEvent).detail.uri);

        if (!uri) {
            return;
        }

        event.stopPropagation();
        const { detail } = event as CustomEvent;
        detail.uri = uri;

        element.dispatchEvent(
            new CustomEvent('der-tm:schema', {
                detail,
                bubbles: true,
                composed: true,
            })
        );
    };

    element.addEventListener('der-tm:schema', handler);

    return () => {
        element.removeEventListener('der-tm:schema', handler);
    };
}

/**
 * Set an initial URI for the given id by combining it with the predefined domain, context, and MFE name.
 * @param id string
 * @returns string
 */
export function setInitialUri(id: string): string {
    return `${URI_DOMAIN}:${URI_CONTEXT}:${MFE_NAME}:${id}`;
}

/**
 * Updates a schema URI by inserting a new segment before the trailing context path.
 * example uri: der:tailormade:roundtrip:der-tm-roundtrip-stage-capability:4711
 * Returns null for malformed URIs, URIs with mismatched domains, or empty new segments.
 * @param uri The source URI.
 * @returns The updated URI or null when input is invalid.
 */
export function updateUri(uri: string): string | null {
    // check if the uri starts with the expected domain
    if (!uri.startsWith(URI_DOMAIN)) {
        return null;
    }

    const parts = uri.split(':');

    if (!parts.length || !URI_DOMAIN_SEGMENTS.length) {
        return null;
    }

    const id = parts.pop();

    if (!id || parts.length < URI_DOMAIN_SEGMENTS.length) {
        return null;
    }

    // remove the domain segments from the beginning of the URI parts
    parts.splice(0, URI_DOMAIN_SEGMENTS.length);

    const firstSegment = parts[0];
    if (!firstSegment) {
        return null;
    }

    const startsWithCapability = firstSegment.startsWith('der-tm');
    const context = startsWithCapability ? firstSegment : URI_CONTEXT;
    const trailingSegments = startsWithCapability ? parts.slice(1) : parts;

    return [...URI_DOMAIN_SEGMENTS, context, MFE_NAME, ...trailingSegments, id].filter(Boolean).join(':');
}
